import React, { useState, useEffect } from 'react'
import {
  ModalBackdrop,
  ModalContainer,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from './styles'
import {
  MdClose,
  MdPalette,
  MdLightMode,
  MdDarkMode,
  MdFilterAlt,
  MdOutlineAdminPanelSettings,
  MdSpeed,
  MdCheckCircle,
  MdFactCheck,
  MdTableChart,
} from 'react-icons/md'
import {
  FaCompass,
  FaArrowLeft,
  FaArrowRight,
  FaCheck,
  FaGlobeAmericas,
  FaBoxes,
  FaFileExcel,
} from 'react-icons/fa'
import { FaRankingStar } from 'react-icons/fa6'
import { TbRadar2 } from 'react-icons/tb'
import { BsStars, BsLightningChargeFill, BsUiChecksGrid } from 'react-icons/bs'
import { PiWarningDiamondFill } from 'react-icons/pi'
import { FiDatabase, FiFolder, FiKey } from 'react-icons/fi'
import { useCustomTheme } from '../../../context/Theme/ThemeContextData'
import { useAuth } from '../../../context/AuthContext'

interface ModalWelcomeUpdateProps {
  isOpen: boolean
  onClose: () => void
  onNavigate?: (pageIndex: number) => void
}

interface ModuleToolItem {
  id: number
  title: string
  desc: string
  icon: React.ReactNode
  iconBg: string
  iconColor: string
  isAllowed: (role: string) => boolean
}

const TOTAL_STEPS = 4

const MODULE_ITEMS: ModuleToolItem[] = [
  {
    id: 0,
    title: 'Análise de Mercado',
    desc: 'Visão de faturamento (R$) e caixas (CX), mapa estadual interativo e comparativos reais contra períodos anteriores.',
    icon: <FaGlobeAmericas />,
    iconBg: 'rgba(56, 189, 248, 0.15)',
    iconColor: '#38bdf8',
    isAllowed: () => true,
  },
  {
    id: 2,
    title: 'Análise de Estoque',
    desc: 'Visão hierárquica por Categoria, Família e SKU, dias de giro por distribuidor e detecção de excesso ou ruptura.',
    icon: <FaBoxes />,
    iconBg: 'rgba(249, 115, 22, 0.15)',
    iconColor: '#f97316',
    isAllowed: () => true,
  },
  {
    id: 11,
    title: 'Validação de Sellout',
    desc: 'Auditoria de sellout e estoque em lote, seleção múltipla de semanas e cálculo proporcional de metas (x/4).',
    icon: <MdFactCheck />,
    iconBg: 'rgba(16, 185, 129, 0.15)',
    iconColor: '#10b981',
    isAllowed: () => true,
  },
  {
    id: 4,
    title: 'Ranking & Potencial IBGE',
    desc: 'Cobertura territorial vs população atendida do IBGE, taxa de atingimento de metas e controle territorial.',
    icon: <FaRankingStar />,
    iconBg: 'rgba(234, 179, 8, 0.15)',
    iconColor: '#eab308',
    isAllowed: (role: string) => role !== 'gestor',
  },
  {
    id: 5,
    title: 'Acompanhamento de Giro',
    desc: 'Monitoramento contínuo de giro de produtos, alertas de estagnação e previsão de abastecimento.',
    icon: <PiWarningDiamondFill />,
    iconBg: 'rgba(244, 63, 94, 0.15)',
    iconColor: '#f43f5e',
    isAllowed: () => true,
  },
  {
    id: 13,
    title: 'Média de SKU & Batalha BCG',
    desc: 'Matriz BCG de sortimento estratégico (Estrelas, Vacas Leiteiras, etc.) e produtividade média por PDV.',
    icon: <TbRadar2 />,
    iconBg: 'rgba(6, 182, 212, 0.15)',
    iconColor: '#06b6d4',
    isAllowed: () => true,
  },
  {
    id: 8,
    title: 'Central de Relatórios',
    desc: 'Vendas detalhadas, Cobertura Mix consolidada por categoria e exportações em Excel executivo corporativo.',
    icon: <FaFileExcel />,
    iconBg: 'rgba(99, 102, 241, 0.15)',
    iconColor: '#6366f1',
    isAllowed: (role: string) => role !== 'gestor' && role !== 'visualizador',
  },
  {
    id: 3,
    title: 'Faseamento',
    desc: 'Acompanhamento de metas comerciais, evolução de sellout mês a mês e trimestres acumulados.',
    icon: <MdTableChart />,
    iconBg: 'rgba(236, 72, 153, 0.15)',
    iconColor: '#ec4899',
    isAllowed: () => true,
  },
  {
    id: 1,
    title: 'Regularidade',
    desc: 'Calendário de acompanhamento de envios diários de arquivos de sellout e estoque com status em tempo real.',
    icon: <BsUiChecksGrid />,
    iconBg: 'rgba(168, 85, 247, 0.15)',
    iconColor: '#a855f7',
    isAllowed: () => true,
  },
  // Telas administrativas / suporte (exclusivas de administrador)
  {
    id: 12,
    title: 'Monitor de Arquivos',
    desc: 'Painel técnico de recebimento, processamento e logs de arquivos FTP dos distribuidores.',
    icon: <FiFolder />,
    iconBg: 'rgba(20, 184, 166, 0.15)',
    iconColor: '#14b8a6',
    isAllowed: (role: string) => role === 'administrador',
  },
  {
    id: 10,
    title: 'Base de Dados & Pendências',
    desc: 'Gestão de carga de dados brutos, tratamento de pendências e conciliação de registros.',
    icon: <FiDatabase />,
    iconBg: 'rgba(139, 92, 246, 0.15)',
    iconColor: '#8b5cf6',
    isAllowed: (role: string) => role === 'administrador',
  },
  {
    id: 9,
    title: 'Controle de Usuários',
    desc: 'Administração de contas, permissões por distribuidor e controle de acessos da plataforma.',
    icon: <MdOutlineAdminPanelSettings />,
    iconBg: 'rgba(245, 158, 11, 0.15)',
    iconColor: '#f59e0b',
    isAllowed: (role: string) => role === 'administrador',
  },
  {
    id: 14,
    title: 'Central GID IA & Relatórios Executivos',
    desc: 'NOVO: Utilize o Assistente Inteligente GID IA para análises rápidas, exporte relatórios em Excel/PDF e programe boletins automáticos por e-mail.',
    icon: <FiKey />,
    iconBg: 'rgba(16, 185, 129, 0.15)',
    iconColor: '#10b981',
    isAllowed: (role: string) =>
      ['administrador', 'suporte', 'conferente'].includes(String(role || '').toLowerCase()),
  },
]

export default function ModalWelcomeUpdate({
  isOpen,
  onClose,
  onNavigate,
}: ModalWelcomeUpdateProps) {
  const [step, setStep] = useState<number>(0)
  const { theme, toggleTheme, menuOrientation, setMenuOrientation } = useCustomTheme()
  const { user } = useAuth()

  const nickname = user?.nickname || 'Usuário'
  const useRole = user?.role || ''

  // Sempre reiniciar da primeira etapa (Passo 1 / step 0) ao abrir o modal
  useEffect(() => {
    if (isOpen) {
      setStep(0)
    }
  }, [isOpen])

  // Fechamento via teclado ESC
  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleFinish()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  if (!isOpen) return null

  const handleNext = () => {
    if (step < TOTAL_STEPS - 1) {
      setStep(step + 1)
    } else {
      handleFinish()
    }
  }

  const handlePrev = () => {
    if (step > 0) {
      setStep(step - 1)
    }
  }

  const handleFinish = () => {
    localStorage.setItem('@GID:seen_welcome_modal_v3', 'true')
    localStorage.setItem('@GID:seen_update_modal_1.2.5.1', 'true')
    localStorage.setItem('@GID:seen_update_modal_1.2.6.0', 'true')
    localStorage.setItem('@GID:seen_update_modal_1.3', 'true')
    setStep(0)
    onClose()
  }

  const handleGoToTool = (pageIndex: number) => {
    handleFinish()
    if (onNavigate) {
      onNavigate(pageIndex)
    }
  }

  // Filtragem estrita por perfil do usuário (RBAC)
  const allowedModules = MODULE_ITEMS.filter((m) => m.isAllowed(useRole))

  return (
    <ModalBackdrop onClick={handleFinish}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        {/* Cabeçalho do Modal sem abas visíveis */}
        <ModalHeader>
          <div className='title-area'>
            <div className='icon-box'>
              <BsStars />
            </div>
            <div className='title-texts'>
              <h2>
                Bem-vindo ao GID 3.0
                <span className='step-pill'>Etapa {step + 1} de {TOTAL_STEPS}</span>
              </h2>
              <p>Guia interativo para você explorar e dominar os recursos da plataforma</p>
            </div>
          </div>
          <button className='close-btn' onClick={handleFinish} title='Fechar (ESC)'>
            <MdClose />
          </button>
        </ModalHeader>

        {/* Conteúdo Dinâmico por Etapa (Navegação transparente sem abas superiores) */}
        <ModalBody>
          {/* Etapa 0: Boas-Vindas & Temas */}
          {step === 0 && (
            <>
              {/* Hero Banner Principal */}
              <div className='hero-banner'>
                <div className='hero-top-badges'>
                  <span className='badge version'>GID 3.0 Oficial</span>
                  <span className='badge feature'>
                    <BsLightningChargeFill style={{ color: '#a6c10b', marginRight: 4 }} />
                    Alta Performance
                  </span>
                  <span className='badge feature'>
                    <MdPalette style={{ marginRight: 4 }} />
                    Tema Claro & Escuro
                  </span>
                </div>

                <h1>
                  Olá, <span className='highlight'>{nickname}</span>! Seja bem-vindo ao GID 3.0
                </h1>

                <p className='lead-text'>
                  Esta é a sua central definitiva de inteligência comercial, sellout, estoques e
                  expansão territorial. Preparamos este guia rápido para apresentar a plataforma e
                  ajudá-lo a aproveitar ao máximo cada ferramenta.
                </p>

                <div className='hero-quick-actions'>
                  <button
                    type='button'
                    className='action-btn primary'
                    onClick={() => setStep(1)}
                  >
                    Iniciar Tour <FaArrowRight size={13} />
                  </button>
                </div>
              </div>

              <h3 className='tab-headline'>
                <MdPalette className='icon-accent' /> Novo Sistema de Temas (Claro & Escuro)
              </h3>
              <p className='tab-subtitle'>
                O ecossistema GID agora possui suporte nativo a dois temas completos. Todas as
                tabelas, gráficos, modais, calendários e painéis analíticos foram calibrados para
                garantir contraste máximo e conforto visual.
              </p>

              <div className='interactive-demo-card'>
                <div className='demo-info'>
                  <strong>Experimente alternar o tema agora mesmo:</strong>
                  <span>Tema atual: <strong>{theme === 'dark' ? 'Tema Escuro (Dark Navy)' : 'Tema Claro (Clean)'}</strong></span>
                </div>
                <div className='demo-actions'>
                  <button
                    type='button'
                    onClick={toggleTheme}
                    style={{
                      background: theme === 'dark' ? '#facc15' : '#1e293b',
                      color: theme === 'dark' ? '#001842' : '#ffffff',
                      border: 'none',
                      padding: '9px 16px',
                      borderRadius: 10,
                      fontWeight: 700,
                      fontFamily: 'Montserrat, sans-serif',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 12,
                    }}
                  >
                    {theme === 'dark' ? <MdLightMode size={16} /> : <MdDarkMode size={16} />}
                    Mudar para {theme === 'dark' ? 'Tema Claro' : 'Tema Escuro'}
                  </button>
                </div>
              </div>

              <div className='features-grid-2'>
                <div className='feature-item-box'>
                  <div className='item-icon'><MdDarkMode /></div>
                  <div className='item-texts'>
                    <h4>Tema Escuro (Dark Navy)</h4>
                    <p>Fundo azul marinho profundo (#001842) com tipografia luminosa e alta imersão, ideal para ambientes executivos.</p>
                  </div>
                </div>
                <div className='feature-item-box'>
                  <div className='item-icon'><MdLightMode /></div>
                  <div className='item-texts'>
                    <h4>Tema Claro (Alto Contraste)</h4>
                    <p>Cartões brancos nítidos, texto ardósia (#0f172a), indicadores destacados e eliminação de caixas escuras residuais.</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Etapa 1: Menu Lateral & Perfil */}
          {step === 1 && (
            <>
              <h3 className='tab-headline'>
                <FaCompass className='icon-accent' /> Menu Lateral Inteligente & Perfil na Base
              </h3>
              <p className='tab-subtitle'>
                A navegação do sistema foi desenhada para oferecer foco absoluto e máxima área útil para seus dados.
              </p>

              <div className='interactive-demo-card'>
                <div className='demo-info'>
                  <strong>Orientação da Navegação:</strong>
                  <span>Atualmente em: <strong>{menuOrientation === 'vertical' ? 'Barra Lateral Vertical' : 'Menu Superior Horizontal'}</strong></span>
                </div>
                <div className='demo-actions'>
                  <button
                    type='button'
                    onClick={() => setMenuOrientation(menuOrientation === 'vertical' ? 'horizontal' : 'vertical')}
                    style={{
                      background: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#0284c7',
                      color: '#ffffff',
                      border: '1px solid rgba(255, 255, 255, 0.25)',
                      padding: '9px 16px',
                      borderRadius: 10,
                      fontWeight: 700,
                      fontFamily: 'Montserrat, sans-serif',
                      cursor: 'pointer',
                      fontSize: 12,
                    }}
                  >
                    Alternar para {menuOrientation === 'vertical' ? 'Menu Superior' : 'Barra Lateral'}
                  </button>
                </div>
              </div>

              <div className='features-grid-2'>
                <div className='feature-item-box'>
                  <div className='item-icon'><FaCompass /></div>
                  <div className='item-texts'>
                    <h4>Usuário Fixado no Rodapé</h4>
                    <p>O perfil do usuário agora fica na base da tela. Ao clicar nele, abre-se um menu suspenso com Tema, Configurações e Logout rápido.</p>
                  </div>
                </div>
                <div className='feature-item-box'>
                  <div className='item-icon'><BsLightningChargeFill /></div>
                  <div className='item-texts'>
                    <h4>Barra Retrátil (Recolher/Expandir)</h4>
                    <p>Clique no botão superior da barra para recolhê-la e expandir sua área de trabalho para tabelas e relatórios densos.</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Etapa 2: Drawer Global de Filtros */}
          {step === 2 && (
            <>
              <h3 className='tab-headline'>
                <MdFilterAlt className='icon-accent' /> Drawer Global de Filtros (Slide-in)
              </h3>
              <p className='tab-subtitle'>
                O botão de Filtros abre uma gaveta lateral deslizante suave da esquerda, mantendo os parâmetros sempre à mão.
              </p>

              <div className='features-grid-2'>
                <div className='feature-item-box'>
                  <div className='item-icon'><MdFilterAlt /></div>
                  <div className='item-texts'>
                    <h4>Seleção Múltipla de Semanas (S1..S4)</h4>
                    <p>Selecione combinações livres de semanas (ex.: S1 + S2). O sistema calcula automaticamente metas proporcionais (x/4) e histórico equivalente.</p>
                  </div>
                </div>
                <div className='feature-item-box'>
                  <div className='item-icon'><MdCheckCircle /></div>
                  <div className='item-texts'>
                    <h4>Estabilidade e Fechamento Ágil</h4>
                    <p>A gaveta protege suas interações internas contra cliques acidentais e pode ser fechada rapidamente clicando fora ou pressionando <strong>ESC</strong>.</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Etapa 3: Módulos & Ferramentas do Sistema (Filtradas rigorosamente por perfil RBAC) */}
          {step === 3 && (
            <>
              <h3 className='tab-headline'>
                <MdSpeed className='icon-accent' /> Módulos & Ferramentas Disponíveis
              </h3>
              <p className='tab-subtitle'>
                Acesse diretamente os módulos do ecossistema liberados para o seu perfil ({useRole || 'padrão'}):
              </p>

              <div className='tools-grid'>
                {allowedModules.map((mod) => (
                  <div
                    key={mod.id}
                    className='tool-card'
                    onClick={() => handleGoToTool(mod.id)}
                    title={`Clique para abrir ${mod.title}`}
                  >
                    <div
                      className='tool-icon-wrap'
                      style={{ background: mod.iconBg, color: mod.iconColor }}
                    >
                      {mod.icon}
                    </div>
                    <h4>{mod.title}</h4>
                    <p>{mod.desc}</p>
                    <div className='tool-footer-link'>
                      Acessar Módulo <FaArrowRight size={10} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Dicas Rápidas */}
              <div className='tips-box'>
                <MdCheckCircle className='tips-icon' />
                <div className='tips-content'>
                  <h4>Dicas de Navegação:</h4>
                  <ul>
                    <li>Clique em qualquer cartão acima para ir diretamente ao módulo desejado.</li>
                    <li>Os filtros aplicados continuam preservados ao transitar entre as telas.</li>
                    <li>Você pode reabrir este guia de boas-vindas sempre que desejar pelo menu do seu usuário no rodapé.</li>
                  </ul>
                </div>
              </div>
            </>
          )}
        </ModalBody>

        {/* Rodapé com Navegação por Etapas */}
        <ModalFooter>
          <div className='footer-left'>
            <span className='step-label'>
              Passo {step + 1} de {TOTAL_STEPS}
            </span>
            <div className='step-dots'>
              {Array.from({ length: TOTAL_STEPS }).map((_, idx) => (
                <div
                  key={idx}
                  className={`dot ${step === idx ? 'active' : ''}`}
                  onClick={() => setStep(idx)}
                  title={`Ir para etapa ${idx + 1}`}
                />
              ))}
            </div>
          </div>

          <div className='nav-buttons'>
            <button
              type='button'
              className='btn-secondary'
              onClick={handlePrev}
              disabled={step === 0}
            >
              <FaArrowLeft size={11} /> Anterior
            </button>

            {step < TOTAL_STEPS - 1 ? (
              <button
                type='button'
                className='btn-primary'
                onClick={handleNext}
              >
                Próximo <FaArrowRight size={11} />
              </button>
            ) : (
              <button
                type='button'
                className='btn-primary'
                onClick={handleFinish}
              >
                Começar a Usar o GID <FaCheck size={11} />
              </button>
            )}
          </div>
        </ModalFooter>
      </ModalContainer>
    </ModalBackdrop>
  )
}
